import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, CardContent, Grid, Skeleton, Typography } from "@mui/material";
import { Chart, registerables } from "chart.js";
import httpClient, { extractApiError } from "../api/httpClient";
import { PageHeader, PagePanel, PageShell, pageTypography } from "../components/PageLayout";
import { useUI } from "../contexts/UIContext";

Chart.register(...registerables);

const fallbackKpis = {
	totalDipendenti: 0,
	turniSettimana: 0,
	orePianificate: 0,
	tassoCopertura: 0,
};

const fallbackCharts = {
	turniMensili: [12, 18, 22, 25, 21, 29],
	feriePerMese: [4, 2, 3, 6, 5, 7],
	distribuzioneRuoli: {
		labels: ["Superadmin", "Admin", "Manager", "Dipendenti"],
		values: [1, 1, 3, 18],
	},
};

function KpiCard({ title, value, subtitle }) {
	return (
		<PagePanel tone="cool" sx={{ height: "100%", minWidth: 0 }}>
			<CardContent sx={{ p: 2.5 }}>
				<Typography sx={{ ...pageTypography.panelLabel, mb: 1 }}>
					{title}
				</Typography>
				<Typography sx={pageTypography.panelValue}>
					{value}
				</Typography>
				<Typography sx={pageTypography.panelCaption}>
					{subtitle}
				</Typography>
			</CardContent>
		</PagePanel>
	);
}

function ChartPanel({ title, tone, height, loading, skeletonHeight, chartRef }) {
	return (
		<PagePanel
			tone={tone}
			sx={{
				p: 2,
				height,
				minWidth: 0,
				display: "flex",
				flexDirection: "column",
			}}
		>
			<Typography sx={{ ...pageTypography.panelTitle, mb: 1.5, flexShrink: 0 }}>
				{title}
			</Typography>
			<Box
				sx={{
					position: "relative",
					flex: 1,
					minHeight: 0,
					minWidth: 0,
					overflow: "hidden",
					"& canvas": {
						display: "block",
						width: "100% !important",
						height: "100% !important",
						maxWidth: "100%",
						maxHeight: "100%",
					},
				}}
			>
				{loading ? <Skeleton variant="rounded" height={skeletonHeight} /> : <canvas ref={chartRef} />}
			</Box>
		</PagePanel>
	);
}

export default function Dashboard() {
	const [loading, setLoading] = useState(true);
	const [kpis, setKpis] = useState(fallbackKpis);
	const [charts, setCharts] = useState(fallbackCharts);
	const { showNotification } = useUI();

	const monthlyChartRef = useRef(null);
	const absencesChartRef = useRef(null);
	const rolesChartRef = useRef(null);

	useEffect(() => {
		let mounted = true;

		const fetchDashboardData = async () => {
			try {
				const [employeesResponse, shiftsResponse, vacationsResponse] = await Promise.all([
					httpClient.get("/dipendenti"),
					httpClient.get("/turni"),
					httpClient.get("/ferie"),
				]);

				if (!mounted) {
					return;
				}

				const employees = employeesResponse?.data || [];
				const shifts = shiftsResponse?.data || [];
				const vacations = vacationsResponse?.data || [];

				const now = new Date();
				const day = now.getDay();
				const diffToMonday = (day + 6) % 7;
				const weekStart = new Date(now);
				weekStart.setHours(0, 0, 0, 0);
				weekStart.setDate(now.getDate() - diffToMonday);
				const weekEnd = new Date(weekStart);
				weekEnd.setDate(weekStart.getDate() + 7);

				const shiftsThisWeek = shifts.filter((shift) => {
					const start = new Date(shift.inizio);
					return start >= weekStart && start < weekEnd;
				});

				const totalHours = shifts.reduce((sum, shift) => {
					const start = new Date(shift.inizio).getTime();
					const end = new Date(shift.fine).getTime();
					if (Number.isNaN(start) || Number.isNaN(end) || end <= start) {
						return sum;
					}
					return sum + (end - start) / 3600000;
				}, 0);

				const coveredEmployees = new Set(
					shifts.map((shift) => shift?.dipendente?.id).filter((id) => id != null)
				).size;
				const coverage = employees.length ? (coveredEmployees / employees.length) * 100 : 0;

				const monthlyShifts = new Array(6).fill(0);
				const monthlyVacations = new Array(6).fill(0);
				for (let i = 0; i < 6; i += 1) {
					const monthDate = new Date(now.getFullYear(), now.getMonth() - (5 - i), 1);
					const month = monthDate.getMonth();
					const year = monthDate.getFullYear();

					monthlyShifts[i] = shifts.filter((shift) => {
						const d = new Date(shift.inizio);
						return d.getMonth() === month && d.getFullYear() === year;
					}).length;

					monthlyVacations[i] = vacations.filter((vacation) => {
						const d = new Date(vacation.dataInizio);
						return d.getMonth() === month && d.getFullYear() === year;
					}).length;
				}

				const mansioniCounter = employees.reduce((acc, employee) => {
					const key = employee.mansione || "N/D";
					acc[key] = (acc[key] || 0) + 1;
					return acc;
				}, {});

				setKpis({
					totalDipendenti: employees.length,
					turniSettimana: shiftsThisWeek.length,
					orePianificate: Number(totalHours.toFixed(1)),
					tassoCopertura: Number(coverage.toFixed(1)),
				});

				setCharts({
					turniMensili: monthlyShifts,
					feriePerMese: monthlyVacations,
					distribuzioneRuoli: {
						labels: Object.keys(mansioniCounter),
						values: Object.values(mansioniCounter),
					},
				});
			} catch (error) {
				showNotification(extractApiError(error), "error");
			} finally {
				if (mounted) {
					setLoading(false);
				}
			}
		};

		fetchDashboardData();
		return () => {
			mounted = false;
		};
	}, [showNotification]);

	const coverageLabel = useMemo(() => `${Number(kpis.tassoCopertura || 0).toFixed(1)}%`, [kpis]);

	useEffect(() => {
		if (loading || !monthlyChartRef.current || !absencesChartRef.current || !rolesChartRef.current) {
			return undefined;
		}

		const monthlyChart = new Chart(monthlyChartRef.current, {
			type: "line",
			data: {
				labels: ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu"],
				datasets: [
					{
						label: "Turni",
						data: charts.turniMensili,
						borderColor: "#00c6ff",
						backgroundColor: "rgba(0, 198, 255, 0.20)",
						fill: true,
						tension: 0.3,
					},
				],
			},
			options: {
				responsive: true,
				maintainAspectRatio: false,
				plugins: { legend: { display: false } },
			},
		});

		const absencesChart = new Chart(absencesChartRef.current, {
			type: "bar",
			data: {
				labels: ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu"],
				datasets: [
					{
						label: "Ferie",
						data: charts.feriePerMese,
						backgroundColor: "rgba(255, 138, 91, 0.65)",
					},
				],
			},
			options: {
				responsive: true,
				maintainAspectRatio: false,
			},
		});

		const roleChart = new Chart(rolesChartRef.current, {
			type: "doughnut",
			data: {
				labels: charts.distribuzioneRuoli.labels,
				datasets: [
					{
						data: charts.distribuzioneRuoli.values,
						backgroundColor: ["#00c6ff", "#ff8a5b", "#7dd3fc"],
						borderWidth: 0,
					},
				],
			},
			options: {
				responsive: true,
				maintainAspectRatio: false,
			},
		});

		return () => {
			monthlyChart.destroy();
			absencesChart.destroy();
			roleChart.destroy();
		};
	}, [loading, charts]);

	return (
		<PageShell>
			<PageHeader
				title="Dashboard"
				subtitle="Panoramica operativa e performance workforce in tempo reale con la stessa struttura visiva introdotta nella pianificazione turni."
			/>
			<Grid container spacing={2} sx={{ minWidth: 0 }}>

			{[ 
				{ title: "Dipendenti attivi", value: kpis.totalDipendenti, subtitle: "Anagrafica validata" },
				{ title: "Turni settimana", value: kpis.turniSettimana, subtitle: "Pianificati nel calendario" },
				{ title: "Ore pianificate", value: kpis.orePianificate, subtitle: "Copertura operativa" },
				{ title: "Tasso copertura", value: coverageLabel, subtitle: "Target: > 95%" },
			].map((item) => (
				<Grid item xs={12} sm={6} lg={3} key={item.title} sx={{ minWidth: 0 }}>
					{loading ? <Skeleton variant="rounded" height={128} /> : <KpiCard {...item} />}
				</Grid>
			))}

			<Grid item xs={12} lg={8} sx={{ minWidth: 0 }}>
				<ChartPanel
					title="Trend turni mensili"
					tone="cool"
					height={360}
					loading={loading}
					skeletonHeight={290}
					chartRef={monthlyChartRef}
				/>
			</Grid>
			<Grid item xs={12} lg={4} sx={{ minWidth: 0 }}>
				<ChartPanel
					title="Distribuzione ruoli"
					tone="warm"
					height={360}
					loading={loading}
					skeletonHeight={290}
					chartRef={rolesChartRef}
				/>
			</Grid>
			<Grid item xs={12} sx={{ minWidth: 0 }}>
				<ChartPanel
					title="Assenze e ferie"
					tone="neutral"
					height={300}
					loading={loading}
					skeletonHeight={220}
					chartRef={absencesChartRef}
				/>
			</Grid>
			</Grid>
		</PageShell>
	);
}
